
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Calendar } from "lucide-react";
import { format } from "date-fns";

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  lastTriage?: {
    urgency: string;
    created_at: string;
    symptoms: string[];
  };
  nextAppointment?: {
    start_time: string;
  };
}

interface PatientTableRowProps {
  patient: Patient;
  onViewPatient: (patientId: string) => void;
}

export function PatientTableRow({ patient, onViewPatient }: PatientTableRowProps) {
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500';
      case 'urgent': return 'bg-orange-500';
      case 'standard': return 'bg-yellow-500';
      case 'non-urgent': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getUrgencyLabel = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'Immediate';
      case 'urgent': return 'Urgent';
      case 'standard': return 'Standard';
      case 'non-urgent': return 'Non-Urgent';
      default: return urgency;
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div>
          <div className="font-medium">
            {patient.first_name} {patient.last_name}
          </div>
          <div className="text-sm text-gray-500">
            Joined {format(new Date(patient.created_at), 'MMM dd, yyyy')}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div>
          <div className="text-sm">{patient.email}</div>
          {patient.phone && (
            <div className="text-sm text-gray-500">{patient.phone}</div>
          )}
        </div>
      </TableCell>
      <TableCell>
        {patient.lastTriage ? (
          <div className="space-y-1">
            <Badge 
              className={`${getUrgencyColor(patient.lastTriage.urgency)} text-white`}
            >
              {getUrgencyLabel(patient.lastTriage.urgency)}
            </Badge>
            <div className="text-xs text-gray-500">
              {format(new Date(patient.lastTriage.created_at), 'MMM dd, yyyy')}
            </div>
            {patient.lastTriage.symptoms.length > 0 && (
              <div className="text-xs text-gray-600 truncate max-w-32">
                {patient.lastTriage.symptoms.slice(0, 2).join(', ')}
                {patient.lastTriage.symptoms.length > 2 && '...'}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No triage</span>
        )}
      </TableCell>
      <TableCell>
        {patient.nextAppointment ? (
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="w-4 h-4 text-blue-500" />
            {format(new Date(patient.nextAppointment.start_time), 'MMM dd, h:mm a')}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No upcoming appointments</span>
        )}
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewPatient(patient.id)}
          className="flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          View
        </Button>
      </TableCell>
    </TableRow>
  );
}
